import { TeliaGrid } from '@teliads/components/react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { getSectionIdFromHash } from '../content-block/utils';
import './AnchorTabs.scss';
import { useHeader } from '../Navigation/contexts/Header.context';

import { useDetectScrollDown } from 'components/voca/the-header/useDetectScrollDown';
import useHorizontalScrollGradient from 'utils/hooks/useHorizontalScrollGradient/useHorizontalScrollGradient';
import { isMobileView } from 'utils/utils';

type LinkType = {
  id: string;
  title: string;
};

interface Props {
  links: LinkType[];
  onTabChange: (id: string) => any;
  gridless?: boolean;
}

export default function AnchorTabs({ links, onTabChange, gridless = false }: Props) {
  const firstTab = links[0].id;

  const [activeTab, setActiveTab] = useState(firstTab);
  const { headerHeight } = useHeader();
  const location = useLocation();

  const tabsHeaderRef = useRef(null);
  useHorizontalScrollGradient(tabsHeaderRef);
  const isScrollingDown = useDetectScrollDown();

  const stickyTopPositionInPx = `${
    isScrollingDown ? headerHeight - headerHeight * 0.4 : headerHeight - 1
  }px`;

  const onTabClick = (id: string) => {
    setActiveTab(id);
    onTabChange(id);
  };

  useEffect(() => {
    const id = getSectionIdFromHash(location);
    if (id) setActiveTab(id);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + headerHeight;
      const activeSection = links.find((link) => {
        const element = document.getElementById(link.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          return scrollPosition >= offsetTop - headerHeight && scrollPosition < offsetBottom;
        }
        return false;
      });
      if (activeSection) {
        setActiveTab(activeSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [links, headerHeight]);

  const renderAnchorTabs = () => {
    return (
      <div className="anchor-tabs" style={{ top: isMobileView() ? 0 : stickyTopPositionInPx }}>
        <ul className="anchor-tabs__list" data-testid="anchor-tabs-list" ref={tabsHeaderRef}>
          {links.map((link) => (
            <li
              key={link.id}
              className={`anchor-tabs__item ${
                activeTab === link.id ? 'anchor-tabs__item--active' : ''
              }`}
              data-testid="anchor-tabs-item"
            >
              <Link
                className="anchor-tabs__link"
                data-testid="anchor-tabs-link"
                to={`#${link.id}`}
                onClick={() => onTabClick(link.id)}
              >
                <span className="anchor-tabs__link-text" data-testid="anchor-tabs-link-text">
                  {link.title}
                </span>

                <span
                  className="anchor-tabs__link-text anchor-tabs__link-text--hidden"
                  data-testid="anchor-tabs-link-text-hidden"
                  aria-hidden
                >
                  {link.title}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderGridlessAnchorTabs = () => {
    return (
      <TeliaGrid
        style={{ top: isMobileView() ? 0 : stickyTopPositionInPx, position: 'sticky', zIndex: 1 }}
      >
        <div className="anchor-tabs">
          <ul className="anchor-tabs__list" data-testid="anchor-tabs-list" ref={tabsHeaderRef}>
            {links.map((link) => (
              <li
                key={link.id}
                className={`anchor-tabs__item ${
                  activeTab === link.id ? 'anchor-tabs__item--active' : ''
                }`}
                data-testid="anchor-tabs-item"
              >
                <Link
                  className="anchor-tabs__link"
                  data-testid="anchor-tabs-link"
                  to={`#${link.id}`}
                  onClick={() => onTabClick(link.id)}
                >
                  <span className="anchor-tabs__link-text" data-testid="anchor-tabs-link-text">
                    {link.title}
                  </span>

                  <span
                    className="anchor-tabs__link-text anchor-tabs__link-text--hidden"
                    data-testid="anchor-tabs-link-text-hidden"
                    aria-hidden
                  >
                    {link.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </TeliaGrid>
    );
  };

  return gridless ? renderGridlessAnchorTabs() : renderAnchorTabs();
}
